// Thin wrapper around Segment's analytics.js snippet (v5.2.0) so the rest of
// the app never touches window.analytics directly. Behavior mirrors the
// official snippet exactly (stub queue + async script load) so this stays
// compatible with anything Segment support gives us to debug against.

const SEGMENT_CDN = "https://cdn.segment.com/analytics.js/v1";

const ANALYTICS_METHODS = [
  "trackSubmit",
  "trackClick",
  "trackLink",
  "trackForm",
  "pageview",
  "identify",
  "reset",
  "group",
  "track",
  "ready",
  "alias",
  "debug",
  "page",
  "screen",
  "once",
  "off",
  "on",
  "addSourceMiddleware",
  "addIntegrationMiddleware",
  "setAnonymousId",
  "addDestinationMiddleware",
  "register",
];

// Methods that should carry Segment's "bpc" page context (canonical url,
// path, referrer, etc.) alongside their normal arguments.
const METHODS_WITH_PAGE_CONTEXT = [
  "track",
  "screen",
  "alias",
  "group",
  "page",
  "identify",
];

function pageContext() {
  const canonical = document.querySelector("link[rel='canonical']");
  return {
    __t: "bpc",
    c: canonical?.getAttribute("href") || undefined,
    p: window.location.pathname,
    u: window.location.href,
    s: window.location.search,
    t: document.title,
    r: document.referrer,
  };
}

// Installs the Segment snippet's stub API (window.analytics) and kicks off
// an async load of the real library. Safe to call once at app startup -
// calls made before the real library loads are queued and flushed once
// analytics.min.js is ready.
export function loadAnalytics(writeKey) {
  if (!writeKey) {
    // eslint-disable-next-line no-console
    console.warn("Segment analytics: no write key configured, skipping load.");
    return;
  }

  try {
    const analytics = (window.analytics = window.analytics || []);

    if (analytics.initialize) return;
    if (analytics.invoked) {
      // eslint-disable-next-line no-console
      console.error("Segment snippet included twice.");
      return;
    }

    analytics.invoked = true;
    analytics.methods = ANALYTICS_METHODS;

    analytics.factory = function (method) {
      return function (...args) {
        if (window.analytics.initialized) {
          return window.analytics[method].apply(window.analytics, args);
        }

        const queued = args.slice();
        if (METHODS_WITH_PAGE_CONTEXT.includes(method)) {
          queued.push(pageContext());
        }
        queued.unshift(method);
        analytics.push(queued);
        return analytics;
      };
    };

    analytics.methods.forEach((method) => {
      analytics[method] = analytics.factory(method);
    });

    analytics.load = function (key, options) {
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.async = true;
      script.setAttribute("data-global-segment-analytics-key", "analytics");
      script.src = `${SEGMENT_CDN}/${key}/analytics.min.js`;

      const firstScript = document.getElementsByTagName("script")[0];
      firstScript.parentNode.insertBefore(script, firstScript);
      analytics._loadOptions = options;
    };

    analytics._writeKey = writeKey;
    analytics.SNIPPET_VERSION = "5.2.0";
    analytics.load(writeKey);
  } catch (error) {
    // Never let a Segment load failure (missing script tags in the DOM,
    // a monkey-patched window.analytics, etc.) block app startup - this
    // runs before React even mounts in index.js.
    // eslint-disable-next-line no-console
    console.error("Segment analytics failed to initialize:", error);
  }
}

// Segment's real analytics.min.js (or a browser extension that patches
// window.analytics) can throw synchronously - e.g. a bad integration config,
// or an adblocker that leaves window.analytics partially stubbed/poisoned
// rather than simply absent. Several of these calls run directly inside
// useEffect (trackPageView/identifyUser in App.js) with no ErrorBoundary
// anywhere in this app, so an uncaught throw there would white-screen the
// whole SPA on every route change. Everything below is guarded so a Segment
// failure can never take down the app - worst case, an event silently fails
// to send.
function safeAnalyticsCall(fn) {
  try {
    fn();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Segment analytics call failed:", error);
  }
}

// Fires a Segment pageview. Call on initial mount and again on every SPA
// route change - Segment's snippet only calls this once automatically on
// load, which isn't enough for a client-side routed app.
export function trackPageView() {
  safeAnalyticsCall(() => window.analytics?.page());
}

// Ties the current session to a known user. Safe to call repeatedly with
// the same id (Segment no-ops on repeats), so callers don't need to dedupe.
export function identifyUser(userId, traits) {
  if (userId == null) return;
  safeAnalyticsCall(() => window.analytics?.identify(String(userId), traits));
}

// Clears the identified user and reverts to a new anonymous id. There's no
// logout flow in this app today, but this is here for when one exists.
export function resetAnalytics() {
  safeAnalyticsCall(() => window.analytics?.reset());
}

// --- Duplicate-fire guard ---------------------------------------------------
// In-memory only (not persisted, not tied to identifyUser/contactId) - resets
// on page reload and never crosses tabs/users. Purely a guard against the
// same UI action firing the same track() call twice in quick succession (e.g.
// a double-click on Add to Cart before the button disables), not a fraud or
// bot-detection mechanism. Segment itself does not dedupe arbitrary custom
// track() payloads server-side, so this has to live on our side.
const DEDUPE_WINDOW_MS = 1000;
const recentEventKeys = new Map();

function isDuplicateEvent(dedupeKey) {
  if (!dedupeKey) return false;
  const now = Date.now();
  const lastFiredAt = recentEventKeys.get(dedupeKey);
  if (lastFiredAt != null && now - lastFiredAt < DEDUPE_WINDOW_MS) {
    return true;
  }
  recentEventKeys.set(dedupeKey, now);

  // Opportunistic cleanup so this map can't grow unbounded over a long
  // session - only runs once the map has enough entries to matter.
  if (recentEventKeys.size > 200) {
    for (const [key, firedAt] of recentEventKeys) {
      if (now - firedAt >= DEDUPE_WINDOW_MS) recentEventKeys.delete(key);
    }
  }
  return false;
}

// All ecommerce/custom events below should call through this instead of
// window.analytics.track directly, so the dedupe guard always applies.
function track(event, properties, dedupeKey) {
  if (isDuplicateEvent(dedupeKey)) return;
  safeAnalyticsCall(() => window.analytics?.track(event, properties));
}

// --- Ecommerce Spec (v2) events -------------------------------------------
// Property names follow Segment's Ecommerce Spec v2:
// https://segment.com/docs/connections/spec/ecommerce/v2/
//
// currency is hardcoded to "USD": verified directly against the dev database
// (not assumed) that this storefront's cart type (Common.CartTypes.CartTypeID
// = 1, "Diamond Publications - USD") resolves to exactly one row in
// Common.CartTypeProcessors, Currency=USD - no CAD/other variant exists for
// this cart type (unlike CartTypeIDs 4/5, which do vary by processor). Every
// row in dev's Store.Carts/Store.CheckoutSessions is USD with no exceptions.
// Flagged to the data/stocking team as something to revisit if multi-currency
// (CAD/AUD) support is ever added for this storefront.
const CURRENCY = "USD";

// Shapes one cart/stock item into a Segment ecommerce "product" line item.
// Used both standalone (Product Viewed/Added) and inside a Checkout
// Started/Order Completed "products" array.
function productLineItem(item) {
  const variant = [item?.color, item?.size]
    .filter((value) => value && value !== "N/A")
    .join(" / ");

  return {
    product_id: item?.productId ?? item?.stockId,
    sku: item?.sku,
    category: item?.category,
    name: item?.title,
    price: item?.memberPrice,
    quantity: item?.quantity ?? 1,
    variant: variant || undefined,
  };
}

// Fires when a shopper views a product detail page.
export function trackProductViewed(item) {
  safeAnalyticsCall(() => {
    if (!item) return;
    const productId = item?.productId ?? item?.stockId;
    track(
      "Product Viewed",
      { ...productLineItem(item), currency: CURRENCY },
      `Product Viewed:${productId}`
    );
  });
}

// Fires once an add-to-cart mutation succeeds. `options` carries the
// quantity/color/size actually submitted, since these can differ from the
// base item's defaults (e.g. a variant picked in the add-to-cart modal).
// `options.reOrder` flags calls sourced from Order Details/My Orders' "Buy
// Again" actions, so these are distinguishable in Segment from a normal
// add-to-cart despite both firing the same event name.
export function trackProductAdded(item, options = {}) {
  safeAnalyticsCall(() => {
    if (!item) return;
    const quantity = options.quantity ?? item?.quantity ?? 1;
    const color = options.color ?? item?.color;
    const size = options.size ?? item?.size;
    const variant = [color, size]
      .filter((value) => value && value !== "N/A")
      .join(" / ");
    const productId = item?.productId ?? item?.stockId;

    track(
      "Product Added",
      {
        ...productLineItem(item),
        quantity,
        variant: variant || undefined,
        cart_id: options.cartId,
        re_order: options.reOrder || undefined,
        currency: CURRENCY,
      },
      `Product Added:${productId}:${variant}:${options.cartId ?? ""}`
    );
  });
}

// Fires once a cart item deletion succeeds. This is the standard signal for
// cart-abandonment analysis (distinct from "Product Saved For Later" below,
// which removes the item from the cart but re-homes it to a list instead of
// discarding it).
export function trackProductRemoved(item, options = {}) {
  safeAnalyticsCall(() => {
    if (!item) return;
    const productId = item?.productId ?? item?.stockId;
    track(
      "Product Removed",
      { ...productLineItem(item), cart_id: options.cartId, currency: CURRENCY },
      `Product Removed:${productId}:${options.cartId ?? ""}`
    );
  });
}

// Not part of Segment's Ecommerce Spec v2 (there's no standard "saved for
// later" event) - a custom event so this app's specific save-for-later
// mechanic (moving/adding an item to the "Saved for Later" list) is still
// visible for abandon-rate analysis alongside Product Removed.
export function trackProductSavedForLater(item) {
  safeAnalyticsCall(() => {
    if (!item) return;
    const productId = item?.productId ?? item?.stockId;
    track(
      "Product Saved For Later",
      { ...productLineItem(item), currency: CURRENCY },
      `Product Saved For Later:${productId}`
    );
  });
}

// Not part of Segment's Ecommerce Spec v2 - a custom event covering this
// app's Favorites feature (useLike/useUnlike in useItemInteractions.js),
// which is functionally a wishlist add and a useful purchase-intent signal
// distinct from an actual add-to-cart.
export function trackProductAddedToWishlist(item) {
  safeAnalyticsCall(() => {
    if (!item) return;
    const productId = item?.productId ?? item?.stockId;
    track(
      "Product Added to Wishlist",
      { ...productLineItem(item), currency: CURRENCY },
      `Product Added to Wishlist:${productId}`
    );
  });
}

// Segment Ecommerce Spec v2 event for in-app search (WWGStorePage's
// client-side searchTerm filtering over already-fetched stock data).
export function trackProductsSearched(query) {
  safeAnalyticsCall(() => {
    const normalized = String(query ?? "").trim();
    if (!normalized) return;
    track(
      "Products Searched",
      { query: normalized },
      `Products Searched:${normalized}`
    );
  });
}

// Segment Ecommerce Spec v2 event for category/subcategory filtering
// (WWGStorePage's activeCategory/activeSubCategory state, driven by
// FilterMenu's category picker). filters is passed through as-is so this
// function doesn't need to know the exact shape the caller builds it in.
export function trackProductListFiltered(filters) {
  safeAnalyticsCall(() => {
    if (!filters || Object.keys(filters).length === 0) return;
    const dedupeKey = JSON.stringify(filters);
    track(
      "Product List Filtered",
      { filters },
      `Product List Filtered:${dedupeKey}`
    );
  });
}

// Fires when a shopper submits their selected payment method to place an
// order (CartPage's onPurchase, right before purchase.mutate). Useful for
// isolating drop-off between payment submission and Order Completed (e.g. a
// declined card), rather than checkout as a whole.
export function trackPaymentInfoEntered({ checkoutSessionId } = {}) {
  safeAnalyticsCall(() => {
    track(
      "Payment Info Entered",
      { order_id: checkoutSessionId, currency: CURRENCY },
      `Payment Info Entered:${checkoutSessionId ?? ""}`
    );
  });
}

// Fires once a checkout session is successfully created.
export function trackCheckoutStarted({
  checkoutSessionId,
  items,
  subTotal,
  tax,
  shipping,
  total,
} = {}) {
  safeAnalyticsCall(() => {
    track(
      "Checkout Started",
      {
        order_id: checkoutSessionId,
        value: total,
        revenue: subTotal,
        tax,
        shipping,
        currency: CURRENCY,
        products: (items ?? []).map(productLineItem),
      },
      `Checkout Started:${checkoutSessionId ?? ""}`
    );
  });
}

// Fires once a purchase mutation succeeds and an order id is known.
export function trackOrderCompleted({
  orderId,
  checkoutSessionId,
  items,
  subTotal,
  tax,
  shipping,
  total,
} = {}) {
  safeAnalyticsCall(() => {
    if (!orderId) return;
    track(
      "Order Completed",
      {
        order_id: orderId,
        checkout_id: checkoutSessionId,
        total,
        revenue: subTotal,
        tax,
        shipping,
        currency: CURRENCY,
        products: (items ?? []).map(productLineItem),
      },
      `Order Completed:${orderId}`
    );
  });
}
